import * as THREE from 'three'
import type { Bot } from 'mineflayer'
import { flattenText, xrText } from './xrText'
import { options } from '../optionsStorage'
import { FollowPanel } from './xrFollowPanel'
import { XRHandRig, faceHandPanel } from './xrHandRig'

/** Title, dialogue and mission packets retain their original Adventure components. */
export class XRServerUI {
  readonly canvas = document.createElement('canvas')
  readonly texture: THREE.CanvasTexture
  readonly plane: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  readonly missionCanvas = document.createElement('canvas')
  readonly missionTexture: THREE.CanvasTexture
  readonly missionPlane: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  readonly titleCanvas = document.createElement('canvas')
  readonly titleTexture: THREE.CanvasTexture
  readonly titlePlane: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  readonly menuCanvas = document.createElement('canvas')
  readonly menuTexture: THREE.CanvasTexture
  readonly menu: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>
  private readonly fade = new THREE.Mesh(new THREE.PlaneGeometry(20, 20), new THREE.MeshBasicMaterial({ color: 0, transparent: true, opacity: 0, depthTest: false, depthWrite: false }))
  private ctx: CanvasRenderingContext2D
  private missionCtx: CanvasRenderingContext2D
  private titleCtx: CanvasRenderingContext2D
  private follower = new FollowPanel()
  private hands?: XRHandRig
  private lastUpdate = 0
  private headPosition = new THREE.Vector3()
  private headDirection = new THREE.Vector3()
  private menuCtx: CanvasRenderingContext2D
  private actionBar: unknown
  private actionAt = 0
  private title: unknown
  private subtitle: unknown
  private titleAt = 0
  private times = { fadeIn: 10, stay: 70, fadeOut: 20 }
  private bars = new Map<string, unknown>()
  private messages: string[] = []
  private selected = -1
  private settings = false
  private listeners: Array<[string, (...args: any[]) => void]> = []
  private ray = new THREE.Raycaster()
  private lastPaint = 0

  constructor(private bot: Bot, private camera: THREE.PerspectiveCamera, scene: THREE.Scene, private actions: { exit: () => void; inventory: () => void; recenter: () => void; locomotion?: () => 'teleport' | 'walk'; toggleLocomotion?: () => void; teleportStatus?: () => string }) {
    this.canvas.width = 1536; this.canvas.height = 384
    this.ctx = this.canvas.getContext('2d')!
    this.texture = new THREE.CanvasTexture(this.canvas); this.texture.colorSpace = THREE.SRGBColorSpace
    this.plane = new THREE.Mesh(new THREE.PlaneGeometry(1.1, 0.275), new THREE.MeshBasicMaterial({ map: this.texture, transparent: true, depthTest: false, depthWrite: false }))
    this.plane.renderOrder = 10500; scene.add(this.plane)
    this.missionCanvas.width = 1120; this.missionCanvas.height = 512
    this.missionCtx = this.missionCanvas.getContext('2d')!
    this.missionTexture = new THREE.CanvasTexture(this.missionCanvas); this.missionTexture.colorSpace = THREE.SRGBColorSpace
    this.missionPlane = new THREE.Mesh(new THREE.PlaneGeometry(0.44, 0.44 * 512 / 1120), new THREE.MeshBasicMaterial({ map: this.missionTexture, transparent: true, depthTest: false, depthWrite: false }))
    this.missionPlane.renderOrder = 10500; camera.add(this.missionPlane)
    this.titleCanvas.width = 1536; this.titleCanvas.height = 768
    this.titleCtx = this.titleCanvas.getContext('2d')!
    this.titleTexture = new THREE.CanvasTexture(this.titleCanvas); this.titleTexture.colorSpace = THREE.SRGBColorSpace
    this.titlePlane = new THREE.Mesh(new THREE.PlaneGeometry(1.8, 0.9), new THREE.MeshBasicMaterial({ map: this.titleTexture, transparent: true, depthTest: false, depthWrite: false }))
    this.titlePlane.position.z = -1.1; this.titlePlane.renderOrder = 10600; camera.add(this.titlePlane)
    this.fade.position.z = -0.3; this.fade.renderOrder = 10400; camera.add(this.fade)
    this.menuCanvas.width = 1024; this.menuCanvas.height = 1000
    this.menuCtx = this.menuCanvas.getContext('2d')!
    this.menuTexture = new THREE.CanvasTexture(this.menuCanvas); this.menuTexture.colorSpace = THREE.SRGBColorSpace
    this.menu = new THREE.Mesh(new THREE.PlaneGeometry(1.2, 1.2 * 1000 / 1024), new THREE.MeshBasicMaterial({ map: this.menuTexture, transparent: true, depthTest: false, depthWrite: false }))
    this.menu.renderOrder = 10800; this.menu.visible = false; scene.add(this.menu)
    const on = (event: string, callback: (...args: any[]) => void) => { (bot._client as any).on(event, callback); this.listeners.push([event, callback]) }
    const prepare = (text: unknown) => { void xrText.prepare(text) }
    const actionBar = (text: unknown) => { if (performance.now() - this.actionAt > 4000) this.follower.reset(); this.actionBar = text; this.actionAt = performance.now(); prepare(text) }
    on('action_bar', packet => actionBar(packet.text))
    on('system_chat', packet => { if (packet.isActionBar || packet.positionId === 2) actionBar(packet.content ?? packet.text ?? packet.formattedMessage) })
    on('set_title_text', packet => { this.title = packet.text; this.titleAt = performance.now(); prepare(packet.text) })
    on('set_title_subtitle', packet => { this.subtitle = packet.text; prepare(packet.text) })
    on('set_title_time', packet => {
      for (const key of ['fadeIn', 'stay', 'fadeOut'] as const) if (packet[key] >= 0) this.times[key] = packet[key]
    })
    on('clear_titles', () => { this.title = null; this.subtitle = null })
    on('boss_bar', packet => {
      if (packet.action === 1) this.bars.delete(packet.entityUUID)
      else if (packet.title !== undefined) { this.bars.set(packet.entityUUID, packet.title); prepare(packet.title) }
    })
    for (const bar of (bot as Bot & { bossBars?: any[] }).bossBars ?? []) { const title = bar._title ?? bar.title; this.bars.set(bar.entityUUID, title); prepare(title) }
    bot.on('messagestr', this.onMessage)
  }

  private onMessage = (message: string) => { this.messages.push(message); this.messages = this.messages.slice(-4) }
  get isOpen() { return this.menu.visible }
  attachHands(hands: XRHandRig) { this.hands = hands }
  recenter() { this.follower.reset() }

  toggle() {
    this.menu.visible = !this.menu.visible
    if (this.menu.visible) {
      this.camera.getWorldPosition(this.menu.position); this.camera.getWorldQuaternion(this.menu.quaternion)
      this.menu.position.add(new THREE.Vector3(0, 0, -1.5).applyQuaternion(this.menu.quaternion))
      this.menu.updateMatrixWorld(true)
      this.paintMenu()
    }
  }

  private buttons() {
    return this.settings ? ['Back', `Sound: ${options.volume}%`, `View distance: ${options.renderDistance} chunks`, 'Recenter view', `Movement: ${this.actions.locomotion?.() === 'walk' ? 'Walk' : 'Teleport'}`, 'Exit VR'] : ['Resume', 'Inventory', 'Options', 'Exit VR']
  }

  click() {
    if (this.selected < 0) return
    if (this.settings) {
      if (this.selected === 0) this.settings = false
      if (this.selected === 1) options.volume = (options.volume + 25) % 125
      if (this.selected === 2) options.renderDistance = options.renderDistance >= 8 ? 2 : options.renderDistance + 2
      if (this.selected === 3) { this.actions.recenter(); this.menu.visible = false }
      if (this.selected === 4) this.actions.toggleLocomotion?.()
      if (this.selected === 5) this.actions.exit()
    } else {
      if (this.selected === 0) this.menu.visible = false
      if (this.selected === 1) { this.menu.visible = false; this.actions.inventory() }
      if (this.selected === 2) this.settings = true
      if (this.selected === 3) this.actions.exit()
    }
    this.paintMenu()
  }

  update(direction: THREE.Vector3, time: number, inventoryOpen: boolean, origin = new THREE.Vector3(), sceneOrigin: THREE.Vector3Like = { x: 0, y: 0, z: 0 }) {
    this.camera.getWorldPosition(this.headPosition); this.headPosition.add(sceneOrigin)
    this.camera.getWorldDirection(this.headDirection)
    this.follower.update(this.headPosition, Math.atan2(-this.headDirection.x, -this.headDirection.z), this.lastUpdate ? (time - this.lastUpdate) / 1000 : 0.016)
    this.lastUpdate = time
    this.plane.position.copy(this.follower.position).sub(sceneOrigin)
    this.plane.quaternion.copy(this.follower.quaternion); this.plane.updateMatrixWorld(true)
    this.plane.visible = !inventoryOpen && !this.isOpen && time - this.actionAt < 4000
    const grip = this.hands?.leftTracked ? this.hands.leftGrip : undefined
    const missionParent = grip ?? this.camera
    if (this.missionPlane.parent !== missionParent) missionParent.add(this.missionPlane)
    this.missionPlane.visible = !inventoryOpen && !this.isOpen && this.bars.size > 0
    if (grip) { this.missionPlane.position.set(-0.17, 0.16, -0.04); this.missionPlane.scale.setScalar(1); faceHandPanel(this.missionPlane, this.camera) }
    else { this.missionPlane.position.set(0, 0.48, -1.2); this.missionPlane.quaternion.identity(); this.missionPlane.scale.setScalar(1.3) }
    if (this.isOpen) {
      this.ray.set(origin, direction)
      const hit = this.ray.intersectObject(this.menu)[0]
      const x = (hit?.uv?.x ?? -1) * 1024
      const y = (1 - (hit?.uv?.y ?? 2)) * 1000
      this.selected = x >= 100 && x < 924 && y >= 160 && (y - 160) % 90 < 76 ? Math.floor((y - 160) / 90) : -1
      if (this.selected >= this.buttons().length) this.selected = -1
    }
    if (time - this.lastPaint < 50) return
    this.lastPaint = time
    this.paint(time)
    if (this.isOpen) this.paintMenu()
  }

  private centered(text: unknown, y: number) { xrText.draw(this.ctx, text, (384 - xrText.measure(this.ctx, text)) / 2, y) }

  private drawTitle(text: unknown, y: number, requestedScale: number) {
    const runs = flattenText(text)
    // The death banner is composed in the SAME component as its 4097px curtain.
    // Keep every spacing run when drawing; only exclude the curtain when fitting
    // visible lettering to the headset's comfortable central field of view.
    const foreground = runs.filter(run => run.font !== 'jerpg:fade' && run.font !== 'jerpg:spacing')
    const maxWidth = runs.some(run => run.font === 'jerpg:you_died') ? 230 : 350
    const scale = Math.min(requestedScale, maxWidth / Math.max(1, xrText.measure(this.titleCtx, foreground)))
    this.titleCtx.save(); this.titleCtx.translate(192, y); this.titleCtx.scale(scale, scale)
    xrText.draw(this.titleCtx, text, -xrText.measure(this.titleCtx, text) / 2, -7, true, ['jerpg:fade'])
    this.titleCtx.restore()
  }

  private paint(time: number) {
    this.ctx.clearRect(0, 0, 1536, 384); this.ctx.save(); this.ctx.scale(4, 4)
    if (time - this.actionAt < 4000 && this.actionBar) this.centered(this.actionBar, 72)
    this.ctx.restore(); this.texture.needsUpdate = true
    this.missionCtx.clearRect(0, 0, 1120, 512); this.missionCtx.save(); this.missionCtx.scale(4, 4)
    let y = 0
    for (const title of [...this.bars.values()].slice(0, 3)) {
      xrText.draw(this.missionCtx, title, (280 - xrText.measure(this.missionCtx, title)) / 2, y)
      y += 38
    }
    this.missionCtx.restore(); this.missionTexture.needsUpdate = true
    this.titleCtx.clearRect(0, 0, 1536, 768); this.titleCtx.save(); this.titleCtx.scale(4, 4)
    const elapsed = (time - this.titleAt) / 50
    const { fadeIn, stay, fadeOut } = this.times
    const opacity = Math.max(0, Math.min(1, elapsed < fadeIn ? elapsed / Math.max(1, fadeIn) : 1 - Math.max(0, elapsed - fadeIn - stay) / Math.max(1, fadeOut)))
    const curtain = flattenText(this.title).some(run => run.font === 'jerpg:fade')
    this.fade.material.opacity = curtain ? opacity : 0
    if (this.title && opacity > 0) {
      this.titleCtx.globalAlpha = opacity
      this.drawTitle(this.title, 96, 4)
      if (this.subtitle) this.drawTitle(this.subtitle, 139, 2)
      this.titleCtx.globalAlpha = 1
    }
    this.titleCtx.restore()
    const failures = [...xrText.failures.entries()].filter(([id]) => id.startsWith('jerpg:'))
    if (failures.length) { this.titleCtx.fillStyle = '#451c16'; this.titleCtx.fillRect(20, 10, 1496, 54); this.titleCtx.fillStyle = '#fff'; this.titleCtx.font = '25px sans-serif'; this.titleCtx.fillText(`JERPG UI asset error: ${failures[0][1]}`, 35, 47, 1460) }
    this.titleTexture.needsUpdate = true
  }

  private paintMenu() {
    const ctx = this.menuCtx
    ctx.clearRect(0, 0, 1024, 1000); ctx.fillStyle = 'rgba(12,22,29,0.98)'; ctx.fillRect(0, 0, 1024, 1000)
    ctx.fillStyle = '#f5edda'; ctx.font = 'bold 48px sans-serif'; ctx.fillText(this.settings ? 'Headset options' : 'JERPG', 100, 78)
    ctx.font = '25px sans-serif'; ctx.fillText('Point at a button, then right trigger · X closes', 100, 122, 825)
    this.buttons().forEach((label, index) => {
      const y = 160 + index * 90
      ctx.fillStyle = this.selected === index ? '#3f765e' : '#23383e'; ctx.fillRect(100, y, 824, 76)
      ctx.fillStyle = '#fff'; ctx.font = '34px sans-serif'; ctx.fillText(label, 133, y + 49)
    })
    ctx.font = '25px sans-serif'; ctx.fillStyle = '#c8d4cf'
    ctx.fillText(this.actions.teleportStatus?.() ?? 'Right grip: interact / use phone', 100, 725, 824)
    ctx.fillText('B/Y: inventory · Left grip: sneak · X: this menu', 100, 765, 824)
    ctx.fillStyle = '#a2b5b7'; ctx.font = '21px sans-serif'
    this.messages.forEach((message, index) => ctx.fillText(message, 100, 824 + index * 34, 824))
    this.menuTexture.needsUpdate = true
  }

  dispose() {
    for (const [event, callback] of this.listeners) (this.bot._client as any).removeListener(event, callback)
    this.bot.removeListener('messagestr', this.onMessage)
    for (const mesh of [this.plane, this.missionPlane, this.titlePlane, this.menu, this.fade]) { mesh.removeFromParent(); mesh.geometry.dispose(); mesh.material.dispose() }
    this.texture.dispose(); this.missionTexture.dispose(); this.titleTexture.dispose(); this.menuTexture.dispose()
  }
}
